import React from "react";
import {
    View,
    Modal,
    TouchableOpacity,
    Image,
    Text,
    StyleSheet,
    ScrollView,
    Animated,
    Dimensions,
    KeyboardAvoidingView,
    Platform,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
    CreateResponsiveStyle,
    DEVICE_SIZES,
    useDeviceSize,
} from "rn-responsive-styles";
import { AppFontStyle } from "../styles/AppFontStyle";
import { AppColors } from "../utility/AppColors";
import { AppImages } from "../utility/AppImages";


interface ModalWrapperProps {
    visible: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    showHeader?: boolean;
    scrollable?: boolean;
    // Height control for mobile drawer
    mobileHeight?: string | number;
    // Custom padding
    padding?: number;
}

/**
 * Common Modal Wrapper Component
 * 
 * Mobile (XS, SM): Bottom drawer style
 * - Slides up from bottom
 * - border-radius: 16px 16px 0 0
 * - border: 1px solid #DC2626
 * - background: linear-gradient(135deg, #111827 0%, #000 100%)
 * - box-shadow: 0 -3px 14px 0 rgba(232, 232, 232, 0.15)
 * - padding: 15px 12px
 * 
 * Web (MD, LG): Centered modal
 * - border-radius: 8px
 * - border: 1.6px solid rgba(239, 68, 68, 0.50)
 * - background: linear-gradient(135deg, #111827 0%, #000 100%)
 * - padding: 20px
 */
export default function ModalWrapper({
    visible,
    onClose,
    title,
    children,
    showHeader = true,
    scrollable = true,
    mobileHeight = "80%",
    padding,
}: ModalWrapperProps) {
    const deviceSize = useDeviceSize();
    const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
    const styles = useStyles();

    const modalContent = (
        <LinearGradient
            colors={["#111827", "#000000"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
                styles.mainView,
                isMobile ? {
                    width: "100%",
                    height: mobileHeight as any,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    borderWidth: 1,
                    borderBottomWidth: 0,
                    borderColor: "#DC2626",
                    padding: padding ?? 15,
                    paddingHorizontal: padding ?? 12,
                    shadowColor: "rgba(232, 232, 232, 0.15)",
                    shadowOffset: { width: 0, height: -3 },
                    shadowOpacity: 1,
                    shadowRadius: 14,
                    elevation: 10,
                } : {
                    maxWidth: 600,
                    borderRadius: 8,
                    borderWidth: 1.6,
                    borderColor: "rgba(239, 68, 68, 0.50)",
                    padding: padding ?? 20,
                },
            ]}
        >
            {/* Handle bar for mobile */}
            {isMobile && (
                <View style={styles.handleBar} />
            )}

            {/* Header with title and close button */}
            {showHeader && (
                <>
                    <View style={styles.headerRow}>
                        <Text style={[
                            isMobile ? AppFontStyle.BOLD_ANYBODY_16 : AppFontStyle.BOLD_ANYBODY_18,
                            { color: AppColors.white, flex: 1 }
                        ]}>
                            {title}
                        </Text>
                        <TouchableOpacity
                            onPress={onClose}
                            style={styles.closeButton}
                        >
                            <Image
                                source={AppImages.icCloseWhite}
                                style={{
                                    height: isMobile ? 20 : 24,
                                    width: isMobile ? 20 : 24,
                                }}
                            />
                        </TouchableOpacity>
                    </View>

                    {/* Separator line */}
                    <View style={styles.separator} />
                </>
            )}

            {/* Content */}
            {scrollable ? (
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{ gap: 12, flexGrow: 1 }}
                    style={{ flex: 1, width: "100%" }}
                >
                    {children}
                </ScrollView>
            ) : (
                <View style={{ gap: 12, flex: 1, width: "100%" }}>
                    {children}
                </View>
            )}
        </LinearGradient>
    );

    return (
        <Modal
            animationType={isMobile ? "slide" : "fade"}
            transparent={true}
            visible={visible}
            onRequestClose={onClose}
        >
            <KeyboardAvoidingView
                style={{ flex: 1 }}
                behavior={Platform.OS === "ios" ? "padding" : undefined}
            >
                <View style={[
                    styles.container,
                    isMobile && { justifyContent: "flex-end" }
                ]}>
                    {/* Backdrop - close on tap */}
                    <TouchableOpacity
                        style={StyleSheet.absoluteFill}
                        activeOpacity={1}
                        onPress={onClose}
                    />
                    {modalContent}
                </View>
            </KeyboardAvoidingView>
        </Modal>
    );
}

const useStyles = CreateResponsiveStyle(
    {
        container: {
            flex: 1,
            backgroundColor: AppColors.blackTrans_000000a8,
            justifyContent: "center",
            alignItems: "center",
        },
        mainView: {
            backgroundColor: "#18151b",
            alignSelf: "center",
            gap: 12,
            alignItems: "flex-start",
        },
        handleBar: {
            width: 40,
            height: 4,
            backgroundColor: "#6B7280",
            borderRadius: 2,
            alignSelf: "center",
            marginBottom: 8,
        },
        headerRow: {
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
        },
        closeButton: {
            padding: 4,
        },
        separator: {
            width: "100%",
            height: 1,
            backgroundColor: "#374151",
        },
    },
    {
        [DEVICE_SIZES.LG]: {
            mainView: {
                maxWidth: 600,
                marginHorizontal: 30,
            },
        },
        [DEVICE_SIZES.MD]: {
            mainView: {
                maxWidth: 600,
                marginHorizontal: 30,
            },
        },
        [DEVICE_SIZES.SM]: {
            container: {
                justifyContent: "flex-end",
            },
            mainView: {
                width: "100%",
                marginHorizontal: 0,
            },
        },
        [DEVICE_SIZES.XS]: {
            container: {
                justifyContent: "flex-end",
            },
            mainView: {
                width: "100%",
                marginHorizontal: 0,
            },
        },
    }
);
